import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { toast } from "react-toastify";
import { NameCell } from "../../components/dashboardcomponents/People/PeopleTable";
import PlatformStatusChip from "../../components/platformAdmin/PlatformStatusChip";
import { PlatformOverviewSkeleton } from "../../components/platformAdmin/PlatformLoadingSkeletons";
import {
  fetchPlatformOverview,
  formatPlatformDate,
} from "../../components/platformAdmin/platformAdminApi";
import {
  cardSx,
  formatNumber,
  inter,
} from "../../components/platformAdmin/platformUi";

function StatCell({ label, value, showDivider }) {
  return (
    <Box
      sx={{
        flex: "1 1 0",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        px: "16px",
        py: "16px",
        boxSizing: "border-box",
        borderRight: showDivider ? "1px solid #E5E9EC" : "none",
      }}
    >
      <Typography
        noWrap
        title={label}
        sx={{
          color: "#405261",
          fontFamily: inter,
          fontSize: 12,
          fontWeight: 400,
          lineHeight: "16px",
          mb: "8px",
        }}
      >
        {label}
      </Typography>
      <Typography
        noWrap
        sx={{
          color: "#061522",
          fontFamily: inter,
          fontSize: 22,
          fontWeight: 500,
          lineHeight: 1.2,
          letterSpacing: "-0.02em",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function StatsRow({ items }) {
  return (
    <Box
      sx={{
        ...cardSx,
        display: "flex",
        flexDirection: "row",
        overflowX: "auto",
        minHeight: 88,
      }}
    >
      {items.map((item, i) => (
        <StatCell
          key={item.label}
          label={item.label}
          value={item.value}
          showDivider={i < items.length - 1}
        />
      ))}
    </Box>
  );
}

const recentGrid =
  "minmax(0, 2fr) minmax(112px, 0.7fr) minmax(112px, 0.7fr)";

export default function PlatformOverview() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const result = await fetchPlatformOverview();
      if (cancelled) return;
      if (!result?.success) {
        toast.error(result?.message || "Failed to load overview");
        setData(null);
      } else {
        setData(result.data || null);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return <PlatformOverviewSkeleton />;
  }

  if (!data) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography sx={{ color: "#405261", fontFamily: inter, fontSize: 14 }}>
          Unable to load overview. Try signing in again.
        </Typography>
      </Box>
    );
  }

  const businesses = data.businesses || {};
  const recent = data.recent_businesses || [];

  return (
    <Box sx={{ p: "24px", display: "flex", flexDirection: "column", gap: 3 }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <StatsRow
          items={[
            { label: "Total businesses", value: formatNumber(businesses.total) },
            { label: "Active", value: formatNumber(businesses.active) },
            { label: "Suspended", value: formatNumber(businesses.suspended) },
            { label: "Banned", value: formatNumber(businesses.banned) },
            { label: "Agents", value: formatNumber(data.agents?.total) },
            { label: "Customers", value: formatNumber(data.customers?.total) },
            { label: "Open chats", value: formatNumber(data.chats?.open) },
            { label: "Open tickets", value: formatNumber(data.tickets?.open) },
          ]}
        />
        <StatsRow
          items={[
            { label: "Registered today", value: formatNumber(businesses.registered_today) },
            { label: "Last 7 days", value: formatNumber(businesses.registered_last_7_days) },
            { label: "Last 30 days", value: formatNumber(businesses.registered_last_30_days) },
          ]}
        />
      </Box>

      <Box sx={{ ...cardSx, overflow: "hidden" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            px: "20px",
            py: "16px",
            borderBottom: "1px solid #E5E9EC",
          }}
        >
          <Typography sx={{ color: "#061522", fontFamily: inter, fontSize: 16, fontWeight: 500, lineHeight: "20px" }}>
            Recently registered
          </Typography>
          <Typography
            component="button"
            type="button"
            onClick={() => navigate("/platform-admin/businesses")}
            sx={{
              border: 0,
              background: "none",
              cursor: "pointer",
              p: 0,
              color: "#148DFD",
              fontFamily: inter,
              fontSize: 13,
              fontWeight: 500,
              "&:hover": { color: "#0077CC" },
            }}
          >
            View all
          </Typography>
        </Box>

        {recent.length === 0 ? (
          <Box sx={{ px: "20px", py: 4 }}>
            <Typography sx={{ color: "#93A1B0", fontFamily: inter, fontSize: 13 }}>
              No recent businesses
            </Typography>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: recentGrid,
                gap: 2,
                px: "20px",
                height: 40,
                alignItems: "center",
                borderBottom: "1px solid #E5E9EC",
                bgcolor: "#F9FAFA",
              }}
            >
              {["Business", "Status", "Registered"].map((h) => (
                <Typography
                  key={h}
                  sx={{
                    color: "#405261",
                    fontFamily: inter,
                    fontSize: 12,
                    fontWeight: 500,
                    lineHeight: "16px",
                  }}
                >
                  {h}
                </Typography>
              ))}
            </Box>

            {recent.map((b, i) => (
              <Box
                key={b._id}
                onClick={() => navigate(`/platform-admin/businesses/${b._id}`)}
                sx={{
                  display: "grid",
                  gridTemplateColumns: recentGrid,
                  gap: 2,
                  px: "20px",
                  minHeight: 56,
                  alignItems: "center",
                  cursor: "pointer",
                  borderBottom: i === recent.length - 1 ? "none" : "1px solid #EEF2F6",
                  "&:hover": { bgcolor: "#F9FAFA" },
                }}
              >
                <NameCell
                  name={b.business_name || "—"}
                  subtitle={b.slug || b.business_email || "—"}
                />
                <Box>
                  <PlatformStatusChip status={(b.status || "active").toLowerCase()} />
                </Box>
                <Typography
                  sx={{ color: "#405261", fontFamily: inter, fontSize: 14, lineHeight: "18px" }}
                >
                  {formatPlatformDate(b.createdAt)}
                </Typography>
              </Box>
            ))}
          </>
        )}
      </Box>
    </Box>
  );
}
